/**
 * Logger Utility
 *
 * Provides a centralized logging interface that respects environment
 * configuration for log levels and console output.
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.debug('Debug message', { data });
 *   logger.info('Info message');
 *   logger.warn('Warning message');
 *   logger.error('Error message', error);
 */

import { env } from '@/config/env';
import type { LogLevel } from '@/config/env';

/**
 * Log level hierarchy (lower index = more verbose)
 */
const LOG_LEVELS: LogLevel[] = ['debug', 'info', 'warn', 'error', 'none'];

/**
 * Color codes for different log levels (for better visibility in console)
 */
const LOG_COLORS = {
  debug: '#6366f1', // Indigo
  info: '#3b82f6', // Blue
  warn: '#f59e0b', // Amber
  error: '#ef4444', // Red
} as const;

/**
 * Emoji icons for different log levels
 */
const LOG_ICONS = {
  debug: '🔍',
  info: 'ℹ️',
  warn: '⚠️',
  error: '❌',
} as const;

/**
 * Check if a log level should be logged based on current configuration
 */
function shouldLog(level: LogLevel): boolean {
  if (level === 'none' || env.logging.level === 'none') {
    return false;
  }

  if (!env.logging.enableConsole) {
    return false;
  }

  const currentLevelIndex = LOG_LEVELS.indexOf(env.logging.level);
  const checkLevelIndex = LOG_LEVELS.indexOf(level);

  return checkLevelIndex >= currentLevelIndex;
}

/**
 * Format timestamp for logs
 */
function getTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Create a styled log message
 */
function createStyledMessage(level: LogLevel, message: string): string[] {
  const icon = LOG_ICONS[level];
  const color = LOG_COLORS[level];
  const timestamp = getTimestamp();

  return [
    `%c${icon} [${level.toUpperCase()}]%c ${timestamp}%c ${message}`,
    `color: ${color}; font-weight: bold`,
    'color: #6b7280; font-size: 0.85em',
    'color: inherit',
  ];
}

/**
 * Logger class with methods for different log levels
 */
class Logger {
  /**
   * Log debug information (verbose, development-only)
   */
  debug(message: string, ...args: unknown[]): void {
    if (!shouldLog('debug')) return;

    const [styledMessage, ...styles] = createStyledMessage('debug', message);
    console.log(styledMessage, ...styles, ...args);
  }

  /**
   * Log informational messages
   */
  info(message: string, ...args: unknown[]): void {
    if (!shouldLog('info')) return;

    const [styledMessage, ...styles] = createStyledMessage('info', message);
    console.log(styledMessage, ...styles, ...args);
  }

  /**
   * Log warning messages
   */
  warn(message: string, ...args: unknown[]): void {
    if (!shouldLog('warn')) return;

    const [styledMessage, ...styles] = createStyledMessage('warn', message);
    console.warn(styledMessage, ...styles, ...args);
  }

  /**
   * Log error messages
   */
  error(message: string, error?: unknown, ...args: unknown[]): void {
    if (!shouldLog('error')) return;

    const [styledMessage, ...styles] = createStyledMessage('error', message);

    if (error instanceof Error) {
      console.error(styledMessage, ...styles, '\n', error, ...args);
      if (error.stack && env.isDevelopment) {
        console.error('Stack trace:', error.stack);
      }
    } else {
      console.error(styledMessage, ...styles, error, ...args);
    }
  }

  /**
   * Log a group of related messages
   */
  group(title: string, level: LogLevel = 'info', collapsed = false): void {
    if (!shouldLog(level)) return;

    const method = collapsed ? console.groupCollapsed : console.group;
    method(`${LOG_ICONS[level]} ${title}`);
  }

  /**
   * End a log group
   */
  groupEnd(): void {
    console.groupEnd();
  }

  /**
   * Log a table (useful for displaying data structures)
   */
  table(data: unknown, level: LogLevel = 'info'): void {
    if (!shouldLog(level)) return;

    console.table(data);
  }

  /**
   * Start a performance timer
   */
  time(label: string): void {
    if (!shouldLog('debug')) return;

    console.time(label);
  }

  /**
   * End a performance timer
   */
  timeEnd(label: string): void {
    if (!shouldLog('debug')) return;

    console.timeEnd(label);
  }

  /**
   * Log application startup information
   */
  logStartup(): void {
    if (!shouldLog('info')) return;

    console.log('%c🚀 Skewb Works Lite', 'color: #E84A2B; font-size: 1.5em; font-weight: bold');
    console.log(
      `%cVersion: ${env.app.version} | Environment: ${env.app.environment}`,
      'color: #6b7280; font-size: 0.9em',
    );
    console.log(`%cBuild Date: ${env.app.buildDate}`, 'color: #6b7280; font-size: 0.9em');
    console.log(''); // Empty line for spacing
  }

  /**
   * Log environment configuration (for debugging)
   */
  logEnvironment(): void {
    if (!shouldLog('debug')) return;

    this.group('Environment Configuration', 'debug', true);
    console.log('App:', env.app);
    console.log('API:', env.api);
    console.log('Features:', env.features);
    console.log('Logging:', env.logging);
    console.log('Performance:', env.performance);
    console.log('UI:', env.ui);
    this.groupEnd();
  }
}

/**
 * Singleton logger instance
 */
export const logger = new Logger();

/**
 * Export for type checking
 */
export type { LogLevel };
